import dayjs from "@/lib/helpers/dayjs";
import { Badge2 } from "@/ui2/atoms/Badge";
import _ from "lodash";

export function getNoteIcon(note) {
  let icon = "bhs-note-icon.svg";
  if (note.provider_type && note.provider_type.startsWith("PRESCRIBE")) {
    icon = "prescriber-note-icon.svg";
  }
  if (note.version === 3) {
    return "care-note-icon.svg";
  } else if (note.version === 4) {
    return "care-note-icon.svg";
  } else if (note.version === 5) {
    return "care-note-icon.svg";
  } else if (note.version === 6 || note.version === 7) {
    return "cm-note.jpg";
  }

  return icon;
}

export function getMetaData(note) {
  let metaData =
    note.note_state === "draft" ? (
      <p className="tw:text-(--brand-bright-purple-600)">
        <b>DRAFT</b>
      </p>
    ) : null;

  if (note.note_type === "Care Management" && note.note_state === "draft") {
    return metaData;
  } else if (note.note_type === "Care Management" || note.note_type === "Advanced Care Activity") {
    // dayjs uses local time by default, equivalent to moment(date).local()
    const submission_date = note.completed_date_time
      ? dayjs(note.completed_date_time).format("MM/DD/YYYY")
      : null;

    const submission_time = note.completed_date_time
      ? dayjs(note.completed_date_time).format("hh:mm A")
      : null;

    return (
      <p className="tw:flex tw:flex-col body-sm">
        <span>{note.signature}</span>

        <span className="tw:contents tw:uppercase">
          <time dateTime={dayjs(note.completed_date_time).toISOString()}>{submission_date}</time>
          <time dateTime={dayjs(note.completed_date_time).format("HH:mm")}>{submission_time}</time>
        </span>
      </p>
    );
  }

  if (note.version === 5 || note.version === 6) {
    const session_date = dayjs(note.session_date, "MM/DD/YYYY").format("MM/DD/YYYY");
    const session_from = dayjs(note.session_from, "hh:mm").format("h:mm a");

    metaData = (
      <p className="tw:flex tw:flex-col body-sm">
        <span>{note.signature}</span>

        <span className="tw:contents tw:uppercase">
          <time dateTime={dayjs(note.session_date, "MM/DD/YYYY").toISOString()}>{session_date}</time>
          <time dateTime={dayjs(note.session_from, "hh:mm").format("HH:mm")}>{session_from}</time>
        </span>
      </p>
    );
  }

  if (note.note_type === "Initial Clinical Assessment") {
    const patient_no_show_time = note.content?.patient_no_show_array?.[0]?.patient_no_show_time;
    
    let submission_date = note.completed_date_time
      ? dayjs.utc(note.completed_date_time).local().format("MM/DD/YYYY")
      : null;
    let submission_date_iso = note.completed_date_time
      ? dayjs.utc(note.completed_date_time).toISOString()
      : null;
    let submission_time = note.completed_date_time
      ? dayjs.utc(note.completed_date_time).local().format("hh:mm A")
      : null;
    let submission_time_iso = note.completed_date_time
      ? dayjs.utc(note.completed_date_time).local().format("HH:mm")
      : null;

    if (patient_no_show_time && !note.completed_date_time) {
      // dayjs uses local time by default, equivalent to moment(date).local()
      submission_date = dayjs(patient_no_show_time).format(
        "MM/DD/YYYY",
      );
      submission_date_iso = dayjs(patient_no_show_time).toISOString();
      submission_time = dayjs(patient_no_show_time).format(
        "hh:mm A",
      );
      submission_time_iso = dayjs(patient_no_show_time).format("HH:mm");
    }

    metaData = (
      <p className="tw:flex tw:flex-col body-sm">
        <span>{note.signature}</span>

        <span className="tw:contents tw:uppercase">
          <time dateTime={submission_date_iso}>{submission_date}</time>
          <time dateTime={submission_time_iso}>{submission_time}</time>
        </span>
      </p>
    );
  }

  return metaData;
}

export function getBadges(note) {
  let badges = [];

  if (!note.is_billable) {
    badges.push(
      <li className="tw:contents" key="badge-non-billable">
        <Badge2 size="xs" variant="navy">
          NON-BILLABLE
        </Badge2>
      </li>,
    );
  }

  if (_.get(note, "content.patient_no_show")) {
    badges.push(
      <li className="tw:contents" key="badge-patient-no-show-1">
        <Badge2 variant="warning" size="xs">
          PATIENT-NO-SHOW
        </Badge2>
      </li>,
    );
  }

  if (
    note.note_type === "Initial Clinical Assessment" &&
    note.content?.patient_no_show_array &&
    !note.completed_date_time
  ) {
    badges.push(
      <li className="tw:contents" key="badge-patient-no-show-2">
        <Badge2 variant="warning" size="xs">
          PATIENT-NO-SHOW
        </Badge2>
      </li>,
    );
  }

  if (note?.call_state === "cancelled") {
    badges.push(
      <li className="tw:contents" key="badge-session-cancelled">
        <Badge2 variant="error" size="xs">
          SESSION CANCELLED
        </Badge2>
      </li>,
    );
  }

  return badges;
}

export function getSubTitle(note) {
  let subTitle = null;

  if (note.version === 5 || note.version === 6) {
    const communicationReason = note.content.reason_for_communication
      .map((reason) => {
        if (reason === "Other (open text)" && note.content.reason_for_communication_other) {
          reason = "Other: " + note.content.reason_for_communication_other;
        }
        return reason;
      })
      .join(", ");

    subTitle = `${note.content.communication_type} - ${communicationReason}`;
  }

  if (
    note.note_type === "Initial Clinical Assessment" &&
    note.content?.patient_no_show_array &&
    !note.completed_date_time
  ) {
    subTitle = null;
  }

  return subTitle;
}

export function getTitle(note) {
  let title = note.title;

  if (note.note_type === "Initial Clinical Assessment") {
    title = "Diagnostic Interview";
  }

  return title;
}
